import { Card } from '@components/common/card'
import { Separator } from '@components/common/separator'
import { Badge } from '@components/common/badge'
import { cn, getContrastTextColor } from '@pma/utils/helpers'
import axios from 'axios'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  PRIORITY_DICTIONARY_ENDPOINT,
  SERVER_URL,
} from '@pma/utils/api-routes'
import { useAuth } from '@stores/auth-store'
import { parseCookies } from 'nookies'
import { ACCESS_TOKEN } from '@pma/utils/constants'
import { DictType } from '@pma/types'
import { Pencil, X } from 'lucide-react'
import { toast } from '@hooks/use-toast'
import PmsLoader from '@components/common/loader'
import useTranslator from '@hooks/use-translator'
import SimpleHoverWithActionButtons from '../hover-action-buttons/SimpleHoverWithActionButtons'
import { useState } from 'react'
import AddPriority from './add-priority-modal'
import UpdatePriority from './update-priority-modal'

const TaskPriorityOptions = () => {
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const { authConfig } = useAuth()
  const { translate } = useTranslator()

  const [selectedPriority, setSelectedPriority] = useState<DictType>({
    name: '',
    color: '#6465ca',
    level: 1,
  })

  const userId = authConfig?.id
  const orgId = authConfig?.last_organization_id
  const getTaskPriority = async () => {
    const response = await axios.get(
      SERVER_URL + PRIORITY_DICTIONARY_ENDPOINT + `?organization_id=${orgId}`,
      {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data, refetch, isLoading } = useQuery({
    queryKey: ['task-priority'],
    queryFn: getTaskPriority,
  })

  const deletePriority = async (id: number) => {
    const response = await axios.delete(
      SERVER_URL + PRIORITY_DICTIONARY_ENDPOINT + `/delete/${id}`,
      {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response?.data
  }

  const deletePriorityMutation = useMutation({
    mutationKey: ['delete-priority'],
    mutationFn: (id: number) => deletePriority(id),
    onSuccess: () => {
      refetch()
      toast({
        title: translate('Success'),
        description: translate('Task priority updated'),
        variant: 'success',
      })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      toast({
        title: translate('Error'),
        description: errorMessage || translate('Unable to delete priority'),
        variant: 'error',
      })
    },
  })

  const updatePriorityApiCall = async (payload: DictType) => {
    const ENDPOINT = PRIORITY_DICTIONARY_ENDPOINT + '/edit'
    const response = await axios.post(SERVER_URL + ENDPOINT, payload, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response?.data
  }

  const updatePriorityMutation = useMutation({
    mutationKey: ['update-priority', 'task'],
    mutationFn: (payload: DictType) => updatePriorityApiCall(payload),
    onSuccess: (data) => {
      if (refetch) refetch()
      toast({
        title: translate('Success'),
        description: translate('Priority updated successfully'),
        variant: 'success',
      })
      setSelectedPriority({
        name: '',
        color: '#6465ca',
      })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate('Error'),
        description: errorMessage || translate('Error updating priority'),
        variant: 'error',
      })
    },
  })

  const handleUpdate = () => {
    if (!selectedPriority?.name) {
      return toast({
        title: translate('Info'),
        description: translate('Enter priority title'),
        variant: 'info',
      })
    }

    const payload = {
      name: selectedPriority.name,
      level: selectedPriority.level,
      color: selectedPriority.color,
      priority_id: selectedPriority.id,
    }

    return updatePriorityMutation.mutate({
      ...payload,
    })
  }

  return (
    <Card className='min-h-[200px] p-3'>
      <div className='flex items-center justify-between'>
        <h3 className='tex-lg font-semibold'>{translate('Tasks Priority')}</h3>
        <AddPriority option='task' revalidate={refetch} />
      </div>

      <Separator className='my-2' />
      {isLoading && <PmsLoader />}

      <div className='mt-4 flex flex-wrap gap-3'>
        {data?.map((priority: DictType) => (
          <SimpleHoverWithActionButtons
            key={priority?.id}
            actionLoading={deletePriorityMutation.isPending || updatePriorityMutation.isPending}
            actionButtons={[
              {
                children: <Pencil />,
                onClick: () => setSelectedPriority(priority),
                popoverContent: (
                  <UpdatePriority
                    option='task'
                    data={selectedPriority}
                    setPriority={setSelectedPriority}
                  />
                ),
                popoverFooterButtons: [
                  {
                    label: translate('Update'),
                    variant: 'default',
                    onClick: handleUpdate,
                  },
                ],
              },
              {
                children: <X />,
                onClick: () => deletePriorityMutation.mutate(priority?.id),
                className: 'text-red-500',
              },
            ]}
          >
            <Badge
              className={cn('relative cursor-pointer text-black')}
              style={{
                background: priority?.color,
                color: getContrastTextColor(priority?.color || "#6465ca"),
              }}
            >
              {priority?.name + ` (${priority?.level})`}
            </Badge>
          </SimpleHoverWithActionButtons>
        ))}
      </div>
    </Card>
  )
}

export default TaskPriorityOptions
